import { Zorb } from '@/components/Header/Zorb'
import { useQuery } from '@tanstack/react-query'
import { AnimatePresence, easeInOut, motion } from 'framer-motion'
import QRCode from 'qrcode'
import { useCallback } from 'react'
import { Actionable, Hidden, Icon, Text, Theme, View, X } from 'reshaped'
import { useLocalStorage } from 'usehooks-ts'

const dismissedStorageKey = 'token-claim-download-cta-dismissed'

export function DownloadAppCta({ size = 'medium' }: { size?: 'small' | 'medium' }) {
  const url = 'https://zora.co/download?ct=claim-token-cta'

  const { data } = useQuery({
    queryKey: ['download-cta', url],
    queryFn: () => QRCode.toDataURL(url, { margin: 0 }),
  })

  const [dismissed, setDismissed] = useLocalStorage(dismissedStorageKey, false)

  const handleDismiss = useCallback(() => {
    setDismissed(true)
  }, [setDismissed])

  return (
    <Hidden hide={{ s: true, m: false }}>
      <View position="fixed" insetBottom={4} insetEnd={4} zIndex={32}>
        <AnimatePresence mode="wait">
          {!dismissed && (
            <motion.div
              key="download-app-cta"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{
                opacity: 0,
                scale: 1.05,
                transition: { duration: 0.6, ease: easeInOut },
              }}
              transition={{ duration: 0.25, delay: 0.25 }}
            >
              <Theme name="zora-ui">
                <View gap={1} align="end">
                  <View backgroundColor="page" borderRadius="circular" shadow="overlay">
                    <Actionable borderRadius="inherit" onClick={handleDismiss}>
                      <View padding={1.5}>
                        <Icon svg={X} size={4} color="neutral-faded" />
                      </View>
                    </Actionable>
                  </View>
                  <View
                    padding={3}
                    backgroundColor="page"
                    gap={1}
                    shadow="overlay"
                    borderRadius="large"
                    justify="center"
                    align="center"
                    width={size === 'small' ? 32 : 44}
                    attributes={{
                      style: {
                        marginRight: 'auto',
                      },
                    }}
                  >
                    <View aspectRatio={1} width="100%" position="relative">
                      <img
                        src={data}
                        alt="Zora app"
                        style={{
                          width: '100%',
                          height: '100%',
                        }}
                      />
                      <View
                        backgroundColor="white"
                        position="absolute"
                        padding={0.5}
                        borderRadius="small"
                        shadow="raised"
                        width={6}
                        attributes={{
                          style: { top: 0, bottom: 0, left: 0, right: 0, margin: 'auto' },
                        }}
                      >
                        <Zorb size={20} />
                      </View>
                    </View>
                    <Text color="primary" weight="semibold">
                      Get the Zora app
                    </Text>
                  </View>
                </View>
              </Theme>
            </motion.div>
          )}
        </AnimatePresence>
      </View>
    </Hidden>
  )
}
